import { Button } from '@/components/Button';
import { FormCard } from '@/components/FormCard';
import { GradientText } from '@/components/GradientText';
import { Link } from '@/components/Link';
import { Text } from '@/components/Text';
import { TextInput } from '@/components/TextInput';
import { AuthLayout } from '@/layouts/AuthLayout';
import { forgotPassword } from '@/services/passwordService';
import i18n from '@/translations/config';
import { ForgotPasswordForm } from '@/types/password';
import { handleError } from '@/utils/handleError';
import { Form, Formik, FormikHelpers } from 'formik';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';

export const ForgotPassword = () => {
  const { t } = useTranslation();
  const lang = i18n.language;
  const navigate = useNavigate();

  const initialValues: ForgotPasswordForm = {
    email: '',
  };

  const validationSchema = yup.object().shape({
    email: yup
      .string()
      .email(t('common:validation.email.format'))
      .required(t('common:validation.email.required')),
  });

  const handleForgotPassword = async (
    values: ForgotPasswordForm,
    { setSubmitting, resetForm, setErrors }: FormikHelpers<ForgotPasswordForm>,
  ) => {
    try {
      const { message } = await forgotPassword(values);
      toast.success(t(message));
      resetForm();
      setTimeout(() => {
        navigate(`/${lang}`);
      }, 2000);
    } catch (error: unknown) {
      handleError(error, setErrors);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout
      title={t('forgotPassword:oups')}
      welcomeText={t('forgotPassword:welcome-text')}
    >
      <FormCard title={t('forgotPassword:title')}>
        <Text className="mb-6 text-center text-sm text-gray-600">
          {t('forgotPassword:info-text')}
        </Text>

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleForgotPassword}
        >
          {({ isSubmitting, touched, errors }) => (
            <Form className="w-full space-y-4 md:space-y-5">
              <div className="space-y-2">
                <TextInput
                  label={t('forgotPassword:label.email')}
                  name="email"
                  type="email"
                  placeholder={t('forgotPassword:placeholder.email')}
                  error={!!touched.email && !!errors.email}
                />
              </div>

              <Button
                type="submit"
                loading={isSubmitting}
                className="mt-4 !w-full bg-gradient-to-r from-sky-600 to-sky-500 py-3 text-white shadow-md hover:from-sky-700 hover:to-sky-600 focus:ring-2 focus:ring-sky-300 md:mt-6"
              >
                {t('forgotPassword:submit')}
              </Button>
            </Form>
          )}
        </Formik>
      </FormCard>

      <Text className="mt-6 text-center text-sm text-gray-600 md:mt-8">
        {t('forgotPassword:sign-in.description')}{' '}
        <Link
          to={`/${lang}`}
          className="font-medium text-sky-600 hover:text-sky-800"
        >
          <GradientText>{t('forgotPassword:sign-in.link')}</GradientText>
        </Link>
      </Text>
    </AuthLayout>
  );
};
